import { create } from "zustand";
import { getDataSource } from "../data";
import { endSession, sessionGuard } from "./session";
import { useWorkspaceStore } from "./workspaceStore";
import type {
  Analytics, ApiKey, Billing, CompactionEntry, ConsoleSettings, DocumentRecord,
  EmbedderProfile, HealthTile, Job, Namespace, NamespaceDraft, Operation,
  OperationsSummary, Repo, TrashEntry, UpsertAck,
} from "../types/domain";
import type { DocumentDraft, KeyDraft, RepoDraft } from "../data/types";

// One store for every resource collection the console renders. Each slice
// carries its own loading flag so a screen can show the mockup's skeleton or
// empty state without a shared spinner.

type Slice =
  | "namespaces" | "documents" | "operations" | "jobs" | "repos" | "keys"
  | "profiles" | "compaction" | "trash" | "analytics" | "billing" | "settings"
  | "health";

interface ConsoleState {
  namespaces: Namespace[];
  documents: DocumentRecord[];
  documentsNamespace: string | null;
  operations: Operation[];
  operationsSummary: OperationsSummary | null;
  jobs: Job[];
  repos: Repo[];
  keys: ApiKey[];
  profiles: EmbedderProfile[];
  compaction: CompactionEntry[];
  trash: TrashEntry[];
  analytics: Analytics | null;
  billing: Billing | null;
  settings: ConsoleSettings | null;
  health: HealthTile[];

  /** The plaintext secret from the most recent create/rotate — shown once. */
  newSecret: string | null;
  /** The last upsert's operation, polled to committed. */
  lastUpsert: (UpsertAck & { namespace: string }) | null;

  loading: Partial<Record<Slice, boolean>>;
  errors: Partial<Record<Slice, string | null>>;

  loadNamespaces: () => Promise<void>;
  loadDocuments: (namespace: string) => Promise<void>;
  loadOperations: () => Promise<void>;
  loadJobs: () => Promise<void>;
  loadRepos: () => Promise<void>;
  loadKeys: () => Promise<void>;
  loadProfiles: () => Promise<void>;
  loadCompaction: () => Promise<void>;
  loadTrash: () => Promise<void>;
  loadAnalytics: () => Promise<void>;
  loadBilling: () => Promise<void>;
  loadSettings: () => Promise<void>;
  loadHealth: () => Promise<void>;

  createNamespace: (draft: NamespaceDraft) => Promise<Namespace>;
  renameNamespace: (id: string, name: string) => Promise<void>;
  deleteNamespace: (id: string) => Promise<void>;
  compactNamespace: (id: string) => Promise<void>;

  upsertDocument: (namespace: string, draft: DocumentDraft) => Promise<void>;
  deleteDocument: (namespace: string, id: string) => Promise<void>;

  cancelJob: (id: string) => Promise<void>;
  restoreTrash: (namespace: string) => Promise<void>;
  purgeTrash: (namespace: string) => Promise<void>;

  connectRepo: (draft: RepoDraft) => Promise<Repo>;
  reindexRepo: (namespace: string) => Promise<void>;
  repairWebhook: (namespace: string) => Promise<void>;
  renameRepo: (id: string, name: string) => Promise<void>;

  createKey: (draft: KeyDraft) => Promise<void>;
  rotateKey: (id: string) => Promise<void>;
  revokeKey: (id: string) => Promise<void>;
  renameKey: (id: string, name: string) => Promise<void>;
  dismissSecret: () => void;

  saveSettings: (patch: Partial<ConsoleSettings>) => Promise<void>;

  /** Drops every cached collection — used on workspace switch and sign-out. */
  resetAll: () => void;
}

function workspaceId(): string {
  const id = useWorkspaceStore.getState().currentId;
  if (!id) throw new Error("no workspace selected");
  return id;
}

const EMPTY = {
  namespaces: [] as Namespace[],
  documents: [] as DocumentRecord[],
  documentsNamespace: null,
  operations: [] as Operation[],
  operationsSummary: null,
  jobs: [] as Job[],
  repos: [] as Repo[],
  keys: [] as ApiKey[],
  profiles: [] as EmbedderProfile[],
  compaction: [] as CompactionEntry[],
  trash: [] as TrashEntry[],
  analytics: null,
  billing: null,
  settings: null,
  health: [] as HealthTile[],
  newSecret: null,
  lastUpsert: null,
};

/**
 * Monotonic ticket per slice: only the newest request for a slice may write its
 * rows or clear its loading flag. Two loads of the same slice can be in flight
 * at once (a screen remount racing a refresh), and the slower one must not win —
 * nor may a bailing request clear a flag that a newer one is still holding.
 */
const seq: Partial<Record<Slice, number>> = {};

/**
 * The operations summary rides alongside the "operations" slice but is a second
 * request, so it needs its own ticket — otherwise an older overlapping
 * loadOperations could overwrite (or null out) a newer summary, leaving tiles
 * that disagree with the list beneath them.
 */
let summarySeq = 0;

export const useConsoleStore = create<ConsoleState>((set, get) => {
  // Wraps a loader so every slice gets consistent loading/error bookkeeping.
  async function run<T>(slice: Slice, fn: () => Promise<T>, apply: (v: T) => Partial<ConsoleState>) {
    const alive = sessionGuard();
    const ticket = (seq[slice] = (seq[slice] ?? 0) + 1);
    set((s) => ({
      loading: { ...s.loading, [slice]: true },
      errors: { ...s.errors, [slice]: null },
    }));
    // A stale session drops the payload but still releases the loading flag —
    // otherwise a slice whose reset landed mid-flight shows its skeleton
    // forever. Safe only because we hold the newest ticket.
    const settle = () => set((s) => ({ loading: { ...s.loading, [slice]: false } }));
    try {
      const value = await fn();
      if (ticket !== seq[slice]) return;
      if (!alive()) return settle();
      set((s) => ({ ...apply(value), loading: { ...s.loading, [slice]: false } }));
    } catch (e) {
      if (ticket !== seq[slice]) return;
      if (!alive()) return settle();
      set((s) => ({
        loading: { ...s.loading, [slice]: false },
        errors: { ...s.errors, [slice]: e instanceof Error ? e.message : String(e) },
      }));
    }
  }

  return {
    ...EMPTY,
    loading: {},
    errors: {},

    loadNamespaces: () =>
      run("namespaces", () => getDataSource().listNamespaces(workspaceId()), (namespaces) => ({ namespaces })),

    loadDocuments: (namespace) =>
      run("documents", () => getDataSource().listDocuments(namespace), (documents) => ({
        documents,
        documentsNamespace: namespace,
      })),

    loadOperations: async () => {
      const ws = workspaceId();
      // Pin the source: resolving it again after the await could straddle a
      // /dev ↔ live switch and mix a list from one with a summary from the other.
      const source = getDataSource();
      const alive = sessionGuard();
      const ticket = ++summarySeq;
      await run("operations", () => source.listOperations(ws), (operations) => ({ operations }));
      try {
        const summary = await source.operationsSummary(ws);
        if (alive() && ticket === summarySeq) set({ operationsSummary: summary });
      } catch {
        if (alive() && ticket === summarySeq) set({ operationsSummary: null });
      }
    },

    loadJobs: () => run("jobs", () => getDataSource().listJobs(workspaceId()), (jobs) => ({ jobs })),
    loadRepos: () => run("repos", () => getDataSource().listRepos(workspaceId()), (repos) => ({ repos })),
    loadKeys: () => run("keys", () => getDataSource().listKeys(workspaceId()), (keys) => ({ keys })),
    loadProfiles: () => run("profiles", () => getDataSource().listProfiles(), (profiles) => ({ profiles })),
    loadCompaction: () => run("compaction", () => getDataSource().compactionQueue(workspaceId()), (compaction) => ({ compaction })),
    loadTrash: () => run("trash", () => getDataSource().listTrash(workspaceId()), (trash) => ({ trash })),
    loadAnalytics: () => run("analytics", () => getDataSource().analytics(workspaceId()), (analytics) => ({ analytics })),
    loadBilling: () => run("billing", () => getDataSource().billing(workspaceId()), (billing) => ({ billing })),
    loadSettings: () => run("settings", () => getDataSource().getSettings(workspaceId()), (settings) => ({ settings })),
    loadHealth: () => run("health", () => getDataSource().dashboardHealth(workspaceId()), (health) => ({ health })),

    createNamespace: async (draft) => {
      const alive = sessionGuard();
      const ns = await getDataSource().createNamespace(workspaceId(), draft);
      // The namespace exists either way; only the optimistic row is dropped, and
      // the next session's loadNamespaces will not show it under the wrong uid.
      if (!alive()) return ns;
      set((s) => ({ namespaces: [ns, ...s.namespaces] }));
      return ns;
    },

    renameNamespace: async (id, name) => {
      const alive = sessionGuard();
      await getDataSource().renameNamespace(id, name);
      if (!alive()) return;
      set((s) => ({
        namespaces: s.namespaces.map((n) => (n.id === id ? { ...n, label: name } : n)),
      }));
    },

    deleteNamespace: async (id) => {
      const alive = sessionGuard();
      await getDataSource().deleteNamespace(id);
      if (!alive()) return;
      set((s) => ({ namespaces: s.namespaces.filter((n) => n.id !== id) }));
      await get().loadTrash();
    },

    compactNamespace: async (id) => {
      const alive = sessionGuard();
      await getDataSource().compactNamespace(id);
      if (!alive()) return;
      set((s) => ({
        compaction: s.compaction.map((c) =>
          c.namespace === id ? { ...c, status: "running" as const } : c,
        ),
      }));
    },

    upsertDocument: async (namespace, draft) => {
      const alive = sessionGuard();
      const ack = await getDataSource().upsertDocument(namespace, draft);
      if (!alive()) return;
      set({ lastUpsert: { ...ack, namespace } });
      await get().loadDocuments(namespace);

      // Poll the operation to its terminal state so the "202 accepted -> queued
      // -> committed" line in the mockup reflects the real pipeline.
      if (!ack.operationId) return;
      const source = getDataSource();
      const deadline = Date.now() + 20000;
      const poll = async (): Promise<void> => {
        // Stop on: reset/workspace switch, timeout, or a newer upsert.
        if (!alive() || Date.now() > deadline) return;
        const current = get().lastUpsert;
        if (!current || current.operationId !== ack.operationId) return;
        try {
          const op = await source.getOperation(namespace, ack.operationId);
          if (!alive()) return;
          if (op && (op.status === "committed" || op.status === "failed")) {
            const latest = get().lastUpsert;
            if (latest?.operationId !== ack.operationId) return;
            set({ lastUpsert: { ...latest, status: op.status } });
            return;
          }
        } catch {
          // A transient status-cache miss is expected; keep polling until the
          // deadline rather than reporting a failure that did not happen.
        }
        setTimeout(() => { void poll(); }, 700);
      };
      setTimeout(() => { void poll(); }, 700);
    },

    deleteDocument: async (namespace, id) => {
      const alive = sessionGuard();
      await getDataSource().deleteDocument(namespace, id);
      if (!alive()) return;
      set((s) => ({ documents: s.documents.filter((d) => d.id !== id) }));
    },

    cancelJob: async (id) => {
      const alive = sessionGuard();
      await getDataSource().cancelJob(id);
      if (!alive()) return;
      set((s) => ({
        jobs: s.jobs.map((j) => (j.id === id ? { ...j, status: "canceled" as const } : j)),
      }));
    },

    restoreTrash: async (namespace) => {
      const alive = sessionGuard();
      await getDataSource().restoreTrash(namespace);
      if (!alive()) return;
      set((s) => ({ trash: s.trash.filter((t) => t.namespace !== namespace) }));
      await get().loadNamespaces();
    },

    purgeTrash: async (namespace) => {
      const alive = sessionGuard();
      await getDataSource().purgeTrash(namespace);
      if (!alive()) return;
      set((s) => ({ trash: s.trash.filter((t) => t.namespace !== namespace) }));
    },

    connectRepo: async (draft) => {
      const alive = sessionGuard();
      const repo = await getDataSource().connectRepo(workspaceId(), draft);
      if (!alive()) return repo;
      set((s) => ({ repos: [repo, ...s.repos] }));
      return repo;
    },

    reindexRepo: async (namespace) => {
      const alive = sessionGuard();
      await getDataSource().reindexRepo(namespace);
      if (!alive()) return;
      set((s) => ({
        repos: s.repos.map((r) =>
          r.namespace === namespace ? { ...r, status: "indexing" as const } : r,
        ),
      }));
    },

    repairWebhook: async (namespace) => {
      const alive = sessionGuard();
      await getDataSource().repairWebhook(namespace);
      if (!alive()) return;
      set((s) => ({
        repos: s.repos.map((r) =>
          r.namespace === namespace ? { ...r, webhook: "healthy" as const } : r,
        ),
      }));
    },

    renameRepo: async (id, name) => {
      const alive = sessionGuard();
      await getDataSource().renameRepo(id, name);
      if (!alive()) return;
      set((s) => ({ repos: s.repos.map((r) => (r.id === id ? { ...r, name } : r)) }));
    },

    createKey: async (draft) => {
      const alive = sessionGuard();
      const { key, secret } = await getDataSource().createKey(workspaceId(), draft);
      // `newSecret` is one-time plaintext. If the session ended while the create
      // was in flight, the secret belongs to an identity that is no longer on
      // screen — drop it rather than render it to whoever is signed in now. The
      // key still exists and can be rotated for a fresh secret.
      if (!alive()) return;
      set((s) => ({ keys: [key, ...s.keys], newSecret: secret }));
    },

    rotateKey: async (id) => {
      const alive = sessionGuard();
      const { key, secret } = await getDataSource().rotateKey(workspaceId(), id);
      if (!alive()) return; // same one-time-secret rule as createKey
      set((s) => ({
        keys: s.keys.map((k) => (k.id === id ? { ...k, masked: key.masked } : k)),
        newSecret: secret,
      }));
    },

    revokeKey: async (id) => {
      const alive = sessionGuard();
      await getDataSource().revokeKey(workspaceId(), id);
      if (!alive()) return;
      set((s) => ({ keys: s.keys.filter((k) => k.id !== id) }));
    },

    renameKey: async (id, name) => {
      const alive = sessionGuard();
      await getDataSource().renameKey(workspaceId(), id, name);
      if (!alive()) return;
      set((s) => ({ keys: s.keys.map((k) => (k.id === id ? { ...k, name } : k)) }));
    },

    dismissSecret: () => set({ newSecret: null }),

    saveSettings: async (patch) => {
      const alive = sessionGuard();
      const settings = await getDataSource().saveSettings(workspaceId(), patch);
      if (!alive()) return;
      set({ settings });
    },

    resetAll: () => {
      endSession();
      set({ ...EMPTY, loading: {}, errors: {} });
    },
  };
});
